import React, { useEffect, useState } from 'react';

const RackCard = ({
  rack,
  showFavoriteButton = true,
  darkMode,
  currentSlide,
  handleNextSlide,
  handlePrevSlide,
  addToCart,
  isInCart,
  toggleFavorite,
  isFavorite,
  selectedBrand,
  selectedModel,
  selectedModification,
  selectedRoofType
}) => {
  // Добавляем локальное состояние для отслеживания статуса корзины
  const [inCartState, setInCartState] = useState(isInCart(rack.id, rack.carId));

  // Обновляем состояние при изменении props
  useEffect(() => {
    setInCartState(isInCart(rack.id, rack.carId));
  }, [isInCart, rack.id, rack.carId]);

  const favorite = isFavorite(rack.id);
  const currentSlideIndex = currentSlide[rack.id] || 0;

  // Добавим проверку на существование адаптера
  const adapterText = rack.needsAdapter && rack.adapterInfo
    ? `+ ${rack.adapterPrice.toLocaleString()} ₽ (адаптер ${rack.adapterInfo.name})`
    : rack.needsAdapter
      ? `+ ${rack.adapterPrice.toLocaleString()} ₽ (адаптер)`
      : '';

  // Обработчик клика по кнопке корзины
  const handleCartClick = () => {
    addToCart(rack);
    setInCartState(!inCartState); // Переключаем локальное состояние
  };

  return (
    <div className={`rounded-lg shadow-md p-4 mb-4 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'
      }`}>
      <div className="relative">
        {/* Image Slider */}
        <div className="relative">
          <img
            src={rack.photo[currentSlideIndex]}
            alt={`${rack.name} - изображение ${currentSlideIndex + 1} из ${rack.photo.length}`}
            className="w-full h-48 object-cover rounded-lg mb-4"
          />

          {rack.photo.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevSlide(rack.id);
                }}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-white bg-opacity-80 flex items-center justify-center shadow-md"
                aria-label="Предыдущее изображение"
              >
                <i className="fas fa-chevron-left" aria-hidden="true"></i>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextSlide(rack.id);
                }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-white bg-opacity-80 flex items-center justify-center shadow-md"
                aria-label="Следующее изображение"
              >
                <i className="fas fa-chevron-right" aria-hidden="true"></i>
              </button>

              <div className="absolute bottom-6 left-0 right-0 flex justify-center" aria-hidden="true">
                {rack.photo.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 mx-1 rounded-full ${index === currentSlideIndex
                      ? 'bg-blue-500'
                      : 'bg-white bg-opacity-60'
                      }`}
                  ></div>
                ))}
              </div>
            </>
          )}
        </div>

        {showFavoriteButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(rack);
            }}
            className="absolute top-2 right-2 w-10 h-10 rounded-full bg-white bg-opacity-80 flex items-center justify-center shadow-md"
            aria-label={favorite ? "Удалить из избранного" : "Добавить в избранное"}
            aria-pressed={favorite}
          >
            <i className={`fas fa-heart text-xl ${favorite ? 'text-red-500' : 'text-gray-400'}`} aria-hidden="true"></i>
          </button>
        )}
      </div>

      <h3 className="font-semibold text-lg">{rack.name}</h3>

      {/* Characteristics */}
      <div className="grid grid-cols-2 gap-2 mt-2 mb-3">
        <div className="flex items-center">
          <i className="fas fa-tag text-gray-500 mr-2" aria-hidden="true"></i>
          <span className="text-sm">Бренд: {rack.brand}</span>
        </div>

        <div className="flex items-center">
          <div
            className="w-4 h-4 rounded-full mr-2"
            style={{
              backgroundColor:
                rack.color === "Чёрный" ? "#000" :
                  rack.color === "Серебристый" ? "#C0C0C0" :
                    rack.color === "Черно-серый" ? "linear-gradient(to right, #000 50%, #C0C0C0 50%)" : "#888"
            }}
            aria-hidden="true"
          ></div>
          <span className="text-sm">Цвет: {rack.color}</span>
        </div>

        <div className="flex items-center">
          <i className="fas fa-grip-lines text-gray-500 mr-2" aria-hidden="true"></i>
          <span className="text-sm">Тип дуги: {rack.bar_type}</span>
        </div>

        <div className="flex items-center">
          <i className="fas fa-weight-hanging text-gray-500 mr-2" aria-hidden="true"></i>
          <span className="text-sm">Грузоподъемность: {rack.capacity}</span>
        </div>

        <div className="flex items-center">
          <i className="fas fa-lock text-gray-500 mr-2" aria-hidden="true"></i>
          <span className="text-sm">Замок: {rack.lock}</span>
        </div>

        <div className="flex items-center">
          <i className="fas fa-globe text-gray-500 mr-2" aria-hidden="true"></i>
          <span className="text-sm">Страна: {rack.country}</span>
        </div>
      </div>

      <div className="mt-2">
        <p className="font-bold text-lg">
          {rack.price.toLocaleString()} ₽
          {rack.needsAdapter && (
            <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {' '}{adapterText}
            </span>
          )}
        </p>
      </div>

      <button
        onClick={handleCartClick}
        className={`mt-4 w-full py-2 rounded-lg ${inCartState
          ? (darkMode ? 'bg-green-700 text-white' : 'bg-green-500 text-white')
          : (darkMode ? 'bg-blue-700 text-white' : 'bg-blue-500 text-white')
          } hover:opacity-90`}
        aria-pressed={inCartState}
      >
        {inCartState ? 'В корзине' : 'В корзину'}
      </button>
    </div>
  );
};

export default RackCard;